import { useCallback, useEffect, useState } from "react";

import type { ApiClient } from "../api/client";
import type { Me, Role, TeamMember } from "../api/types";
import { ExcelIcon } from "../components/ExcelIcon";
import { MonthNav } from "../components/MonthNav";
import { toMonthString, type MonthRef } from "../lib/dates";

interface Props {
  api: ApiClient;
  me: Me;
  month: MonthRef;
  onChanged: () => void | Promise<void>;
}

const ROLE_LABELS: Record<Role, string> = {
  employee: "Employee",
  subadmin: "Sub-admin",
  admin: "Administrator",
};

export function TeamPage({ api, me, month, onChanged }: Props): JSX.Element {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [monthRef, setMonthRef] = useState<MonthRef>(month);

  const monthString = toMonthString(monthRef);

  const load = useCallback(async () => {
    setError(null);
    try {
      setMembers(await api.team(monthString));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Could not load the team.");
    }
  }, [api, monthString]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>, message?: string): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      await action();
      await load();
      await onChanged();
      if (message) setNotice(message);
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Something went wrong.");
    }
  }

  async function exportMonth(): Promise<void> {
    setError(null);
    try {
      const { blob, filename } = await api.exportMonth(monthString);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (exportError) {
      setError(exportError instanceof Error ? exportError.message : "Export failed.");
    }
  }

  return (
    <div className="page">
      <MonthNav month={monthRef} onStep={(delta) => setMonthRef((current) => ({
        year: current.year + Math.floor((current.month - 1 + delta) / 12),
        month: (((current.month - 1 + delta) % 12) + 12) % 12 + 1,
      }))} onChange={setMonthRef}>
        <button
          type="button"
          className="primary icon-button"
          onClick={() => void exportMonth()}
        >
          <ExcelIcon />
          <span>Export to Excel</span>
        </button>
      </MonthNav>

      {error && <p className="error">{error}</p>}
      {notice && <p className="notice">{notice}</p>}

      <section className="card">
        <table className="team-table">
          <thead>
            <tr>
              <th scope="col">Person</th>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col">Hours this month</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr key={member.id} className={member.is_active ? "" : "inactive"}>
                <td>
                  <div className="person-cell">
                    <span className="avatar" aria-hidden="true">
                      {member.initials}
                    </span>
                    {editingName === member.id ? (
                      <input
                        className="inline-edit"
                        value={draftName}
                        autoFocus
                        onChange={(event) => setDraftName(event.target.value)}
                        onBlur={() => setEditingName(null)}
                        onKeyDown={(event) => {
                          if (event.key === "Escape") setEditingName(null);
                          if (event.key === "Enter") {
                            const name = draftName.trim();
                            setEditingName(null);
                            if (name && name !== member.display_name) {
                              void run(() =>
                                api.updateMember(member.id, { display_name: name }),
                              );
                            }
                          }
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        className="link name-button"
                        title="Click to rename"
                        onClick={() => {
                          setEditingName(member.id);
                          setDraftName(member.display_name);
                        }}
                      >
                        {member.display_name}
                        {member.id === me.id && <span className="muted small"> (you)</span>}
                      </button>
                    )}
                  </div>
                </td>
                <td className="muted">{member.email}</td>
                <td>
                  <select
                    value={member.role}
                    onChange={(event) =>
                      void run(() =>
                        api.updateMember(member.id, { role: event.target.value as Role }),
                      )
                    }
                  >
                    {(Object.keys(ROLE_LABELS) as Role[]).map((role) => (
                      <option key={role} value={role}>
                        {ROLE_LABELS[role]}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="numeric">{member.duration_label}</td>
                <td>
                  <button
                    type="button"
                    className={member.is_active ? "ghost small" : "primary small"}
                    onClick={() =>
                      void run(() =>
                        api.updateMember(member.id, { is_active: !member.is_active }),
                      )
                    }
                  >
                    {member.is_active ? "Deactivate" : "Reactivate"}
                  </button>
                  {!member.has_signed_in && (
                    <span className="pill invited" title="Invited but has not signed in yet">
                      Invited
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <AddPersonForm
        onAdd={(input) =>
          run(
            () => api.addMember(input),
            `${input.display_name} can now sign in with their work account.`,
          )
        }
      />
    </div>
  );
}

function AddPersonForm({
  onAdd,
}: {
  onAdd: (input: { email: string; display_name: string; role: Role }) => Promise<void>;
}): JSX.Element {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("employee");
  const [busy, setBusy] = useState(false);

  return (
    <section className="card">
      <h2>Add someone</h2>
      <p className="muted small">
        They sign in with their own work account — there is no PIN to share.
      </p>
      <form
        className="add-person"
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          void onAdd({ email: email.trim(), display_name: name.trim(), role })
            .then(() => {
              setEmail("");
              setName("");
              setRole("employee");
            })
            .finally(() => setBusy(false));
        }}
      >
        <label className="field">
          <span>Name</span>
          <input value={name} required onChange={(event) => setName(event.target.value)} />
        </label>
        <label className="field">
          <span>Work email</span>
          <input
            type="email"
            value={email}
            required
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <label className="field small">
          <span>Role</span>
          <select value={role} onChange={(event) => setRole(event.target.value as Role)}>
            {(Object.keys(ROLE_LABELS) as Role[]).map((value) => (
              <option key={value} value={value}>
                {ROLE_LABELS[value]}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="primary" disabled={busy}>
          Add person
        </button>
      </form>
    </section>
  );
}
